import {
  Component,
  EventEmitter,
  Output,
  OnInit,
  OnDestroy,
  Input,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_SELECT_CONFIG, MatSelect, MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { provideNativeDateAdapter } from '@angular/material/core';
import * as XLSX from 'xlsx';
import { ApiService } from '../../services/api.service';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { PackageService } from '../../services/package.service';
import { Package } from '../../shared/types';
import { sanitizeErrorMessage } from '../../shared/utils/error.util';
import { getUserTimezone } from '../../shared/utils/date.util';
import { SEGMENT_CRITERIA_OPTIONS } from '../segment-criteria.constants';

export type SegmentCreateMethod = 'rule' | 'upload';

const EXCEL_ACCEPT_EXTENSIONS = ['.xlsx', '.xls', '.csv'];
const EXCEL_MAX_BYTES = 10 * 1024 * 1024; // 10MB UI guard; server cap comes later

/** Max panel height — keep in sync with `.segment-criteria-select-panel` SCSS. */
const CRITERIA_SELECT_PANEL_MAX_HEIGHT_PX = 200;
const CRITERIA_SELECT_PANEL_GAP_PX = 4;
const CRITERIA_SELECT_PANEL_MIN_HEIGHT_PX = 80;

@Component({
  selector: 'app-add-segment-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatSelectModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
  ],
  // The criteria date pickers need a date adapter; native is enough for these plain dates.
  providers: [
    provideNativeDateAdapter(),
    {
      provide: MAT_SELECT_CONFIG,
      useValue: {
        overlayPanelClass: 'segment-criteria-select-overlay',
        panelWidth: 'auto',
      },
    },
  ],
  templateUrl: './add-segment-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './add-segment-modal.component.scss',
})
export class AddSegmentModalComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isEditMode = false;
  @Input() segmentToEdit: any = null;
  /** Campaign platform already selected in the editor (e.g. sms, email). */
  @Input() campaignPlatform: string | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<any>();

  isLoading = false;

  /** Default Segment rule — existing create/edit path unchanged. */
  createMethod: SegmentCreateMethod = 'rule';
  selectedExcelFile: File | null = null;
  excelFileError: string | null = null;
  isDragOver = false;

  // Store original form data for edit mode change detection
  private originalFormData: any = null;

  // Track which fields have been touched for validation
  touchedFields = {
    name: false,
    description: false,
  };

  touchedCriteria: Record<number, Record<string, boolean>> = {};

  // Track which dropdown is currently open
  openDropdownId: string | null = null;

  /** Active criteria mat-select — used to keep the panel aligned while the modal body scrolls. */
  private activeCriteriaSelect: MatSelect | null = null;

  segmentForm = {
    name: '',
    description: '',
    criteria: [
      {
        type: '',
        condition: '',
        value: null as Date | string | null,
        startDate: null as Date | string | null,
        endDate: null as Date | string | null,
        selectedPackages: [] as string[],
        selectedWashTypes: [] as string[],
        selectedVehicleMakes: [] as string[],
      },
    ],
    logicalOperators: [] as string[], // Between row i and i+1; length === criteria.length - 1
  };

  // Get criteria types from criteriaOptions
  get criteriaTypes() {
    return this.criteriaOptions.map(option => ({
      label: option.label,
      value: option.value,
    }));
  }

  // Get conditions based on selected criteria type
  getConditionsForCriteria(criteriaType: string) {
    const criteria = this.criteriaOptions.find(option => option.value === criteriaType);
    return criteria ? criteria.options : [];
  }

  // Get value type for selected condition
  getValueTypeForCondition(criteriaType: string, condition: string) {
    const criteria = this.criteriaOptions.find(option => option.value === criteriaType);
    if (criteria && criteria.options) {
      const conditionOption = criteria.options.find(opt => opt && opt.value === condition);
      return conditionOption?.value_type || '';
    }
    return '';
  }

  ageRangeOptions = [
    { label: '18-25', value: '18-25' },
    { label: '26-35', value: '26-35' },
    { label: '36-45', value: '36-45' },
    { label: '46-55', value: '46-55' },
    { label: '55+', value: '55+' },
  ];

  // Package options for package selection - loaded from service
  packageOptions: { label: string; value: string }[] = [];

  // Car wash type options
  carWashTypeOptions = [
    { label: 'Single Wash', value: 'single_wash' },
    { label: 'Monthly Membership', value: 'monthly_membership' },
  ];

  // Vehicle make options - Popular USA vehicle makes
  vehicleMakeOptions = [
    { label: 'Abarth', value: 'abarth' },
    { label: 'Acura', value: 'acura' },
    { label: 'Alfa Romeo', value: 'alfa_romeo' },
    { label: 'Alpine', value: 'alpine' },
    { label: 'Aston Martin', value: 'aston_martin' },
    { label: 'Audi', value: 'audi' },
    { label: 'BMW', value: 'bmw' },
    { label: 'Bentley', value: 'bentley' },
    { label: 'Bugatti', value: 'bugatti' },
    { label: 'Buick', value: 'buick' },
    { label: 'Cadillac', value: 'cadillac' },
    { label: 'Chevrolet', value: 'chevrolet' },
    { label: 'Chrysler', value: 'chrysler' },
    { label: 'Dodge', value: 'dodge' },
    { label: 'Ferrari', value: 'ferrari' },
    { label: 'Fiat', value: 'fiat' },
    { label: 'Ford', value: 'ford' },
    { label: 'Genesis', value: 'genesis' },
    { label: 'GMC', value: 'gmc' },
    { label: 'Honda', value: 'honda' },
    { label: 'Hummer', value: 'hummer' },
    { label: 'Hyundai', value: 'hyundai' },
    { label: 'Infiniti', value: 'infiniti' },
    { label: 'Isuzu', value: 'isuzu' },
    { label: 'Jaguar', value: 'jaguar' },
    { label: 'Jeep', value: 'jeep' },
    { label: 'Kia', value: 'kia' },
    { label: 'Lamborghini', value: 'lamborghini' },
    { label: 'Land Rover', value: 'land_rover' },
    { label: 'Lexus', value: 'lexus' },
    { label: 'Lincoln', value: 'lincoln' },
    { label: 'Lotus', value: 'lotus' },
    { label: 'Lucid', value: 'lucid' },
    { label: 'Maserati', value: 'maserati' },
    { label: 'Mazda', value: 'mazda' },
    { label: 'McLaren', value: 'mclaren' },
    { label: 'Mercedes-Benz', value: 'mercedes_benz' },
    { label: 'Mercury', value: 'mercury' },
    { label: 'Mini', value: 'mini' },
    { label: 'Mitsubishi', value: 'mitsubishi' },
    { label: 'Nissan', value: 'nissan' },
    { label: 'Oldsmobile', value: 'oldsmobile' },
    { label: 'Plymouth', value: 'plymouth' },
    { label: 'Polestar', value: 'polestar' },
    { label: 'Pontiac', value: 'pontiac' },
    { label: 'Porsche', value: 'porsche' },
    { label: 'Ram', value: 'ram' },
    { label: 'Rivian', value: 'rivian' },
    { label: 'Rolls-Royce', value: 'rolls_royce' },
    { label: 'Saturn', value: 'saturn' },
    { label: 'Scion', value: 'scion' },
    { label: 'Smart', value: 'smart' },
    { label: 'Spyker', value: 'spyker' },
    { label: 'Subaru', value: 'subaru' },
    { label: 'Suzuki', value: 'suzuki' },
    { label: 'Tesla', value: 'tesla' },
    { label: 'Toyota', value: 'toyota' },
    { label: 'Volkswagen', value: 'volkswagen' },
    { label: 'Volvo', value: 'volvo' },
  ];

  // Vehicle make search and pagination properties
  vehicleMakeSearchTerm = '';
  vehicleMakeShowAll = false;
  vehicleMakeItemsPerPage = 6;
  criteriaOptions = SEGMENT_CRITERIA_OPTIONS;

  onClose() {
    this.close.emit();
  }

  // Handle dropdown show/hide events to ensure only one is open at a time
  onDropdownShow(dropdownId: string) {
    this.openDropdownId = dropdownId;
  }

  onDropdownHide() {
    this.openDropdownId = null;
  }

  /**
   * Criteria Type / Conditions selects: Material positions against the viewport, so the panel
   * can open downward over the modal footer. Re-anchor within `.modal-body` and flip upward
   * when there is not enough room below the trigger.
   */
  onCriteriaSelectOpenedChange(isOpen: boolean, dropdownId: string, select: MatSelect): void {
    if (!isOpen) {
      this.activeCriteriaSelect = null;
      this.onDropdownHide();
      return;
    }

    this.onDropdownShow(dropdownId);
    this.activeCriteriaSelect = select;

    // Wait for CDK to attach/position the overlay, then constrain to the modal body.
    // A follow-up timeout covers flexible-dimension settle that can overwrite our styles.
    requestAnimationFrame(() => {
      this.positionCriteriaSelectPanel(select);
      setTimeout(() => this.positionCriteriaSelectPanel(select), 0);
    });
  }

  onModalBodyScroll(): void {
    if (this.activeCriteriaSelect?.panelOpen) {
      this.positionCriteriaSelectPanel(this.activeCriteriaSelect);
    }
  }

  private positionCriteriaSelectPanel(select: MatSelect): void {
    if (!select.panelOpen) {
      return;
    }

    const triggerEl = select.trigger?.nativeElement as HTMLElement | undefined;
    const panelEl = select.panel?.nativeElement as HTMLElement | undefined;
    if (!triggerEl || !panelEl) {
      return;
    }

    const selectEl = triggerEl.closest('mat-select') as HTMLElement | null;
    const modalBody = triggerEl.closest('.modal-body') as HTMLElement | null;
    const overlayPane = panelEl.closest('.cdk-overlay-pane') as HTMLElement | null;
    if (!selectEl || !modalBody || !overlayPane) {
      return;
    }

    const triggerRect = selectEl.getBoundingClientRect();
    const bodyRect = modalBody.getBoundingClientRect();

    const spaceBelow = bodyRect.bottom - triggerRect.bottom - CRITERIA_SELECT_PANEL_GAP_PX;
    const spaceAbove = triggerRect.top - bodyRect.top - CRITERIA_SELECT_PANEL_GAP_PX;

    const openAbove = spaceBelow < CRITERIA_SELECT_PANEL_MAX_HEIGHT_PX && spaceAbove > spaceBelow;
    // Cap to actual available space (non-negative). Do not force the min height when
    // it would exceed the modal-body boundary; still respect the max when space allows.
    const availableSpace = Math.max(0, openAbove ? spaceAbove : spaceBelow);
    const maxHeight = Math.min(
      CRITERIA_SELECT_PANEL_MAX_HEIGHT_PX,
      availableSpace >= CRITERIA_SELECT_PANEL_MIN_HEIGHT_PX
        ? availableSpace
        : Math.min(availableSpace, CRITERIA_SELECT_PANEL_MIN_HEIGHT_PX)
    );

    panelEl.style.maxHeight = `${maxHeight}px`;
    panelEl.style.overflowY = 'auto';

    const panelHeight = Math.min(panelEl.scrollHeight, maxHeight);
    const top = openAbove
      ? triggerRect.top - panelHeight - CRITERIA_SELECT_PANEL_GAP_PX
      : triggerRect.bottom + CRITERIA_SELECT_PANEL_GAP_PX;

    // Fixed coords keep the panel aligned to the trigger and inside the modal body bounds
    // (Material's default strategy only knows about the viewport, not the footer).
    overlayPane.style.position = 'fixed';
    overlayPane.style.top = `${top}px`;
    overlayPane.style.left = `${triggerRect.left}px`;
    overlayPane.style.width = `${triggerRect.width}px`;
    overlayPane.style.maxWidth = `${triggerRect.width}px`;
    overlayPane.style.bottom = 'auto';
    overlayPane.style.margin = '0';
    overlayPane.style.transform = 'none';

    panelEl.classList.toggle('mat-mdc-select-panel-above', openAbove);
  }

  // Blur event handlers for validation
  onNameBlur() {
    this.touchedFields.name = true;
  }

  onDescriptionBlur() {
    this.touchedFields.description = true;
  }

  onCriteriaBlur(criteriaIndex: number, field: string) {
    if (!this.touchedCriteria[criteriaIndex]) {
      this.touchedCriteria[criteriaIndex] = {};
    }
    this.touchedCriteria[criteriaIndex][field] = true;
  }

  get platformHintLabel(): string {
    const platform = (this.campaignPlatform || '').toLowerCase();
    if (platform === 'sms') {
      return 'SMS campaign';
    }
    if (platform === 'email') {
      return 'Email campaign';
    }
    if (platform) {
      return `${platform} campaign`;
    }
    return '';
  }

  onCreateMethodChange(method: SegmentCreateMethod): void {
    if (this.createMethod === method) {
      return;
    }
    this.createMethod = method;
    this.excelFileError = null;
    if (method === 'rule') {
      this.clearSelectedExcelFile();
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.setSelectedExcelFile(file);
    }
  }

  onExcelFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.setSelectedExcelFile(file);
    }
    // Allow re-selecting the same file name.
    input.value = '';
  }

  clearSelectedExcelFile(): void {
    this.selectedExcelFile = null;
    this.excelFileError = null;
  }

  formatFileSize(bytes: number): string {
    if (!bytes) {
      return '0 B';
    }
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  downloadSampleFile(): void {
    this.downloadWorkbook(
      [
        {
          name: 'Jane Doe',
          phone_number: '5551234567',
          email: 'jane@example.com',
          year: '2020',
          make: 'Toyota',
          model: 'Camry',
        },
        {
          name: 'John Smith',
          phone_number: '5559876543',
          email: 'john@example.com',
          year: '2018',
          make: 'Honda',
          model: 'Civic',
        },
        {
          name: 'No Phone Row',
          phone_number: '',
          email: 'nophone@example.com',
          year: '2019',
          make: 'Ford',
          model: 'F-150',
        },
      ],
      'segment_upload_sample.xlsx'
    );
  }

  onSave() {
    if (this.isEditMode) {
      this.updateSegment();
      return;
    }
    if (this.createMethod === 'upload') {
      this.createUploadSegment();
      return;
    }
    this.createSegment();
  }

  onClear() {
    this.resetForm({
      preserveUploadMethod: this.isEditMode && this.segmentToEdit?.source_type === 'upload',
      keepOriginalFormData: this.isEditMode,
    });
    this.touchedFields = {
      name: false,
      description: false,
    };
    this.touchedCriteria = {};
  }

  // Check if form has any data to enable/disable Clear button
  /**
   * Check whether the segment form contains any user-provided data (name, description, or any populated criteria).
   * @example
   * hasFormData()
   * true
   * @param {void} none - No arguments.
   * @returns {boolean} True if any form field or criteria value is populated; otherwise false.
   */
  get hasFormData(): boolean {
    // Check if name or description has data
    if (this.segmentForm.name.trim() !== '' || this.segmentForm.description.trim() !== '') {
      return true;
    }

    if (this.createMethod === 'upload' && this.selectedExcelFile) {
      return true;
    }

    if (this.createMethod === 'upload') {
      return false;
    }

    // Check if any criteria has data
    return this.segmentForm.criteria.some(criteria => {
      // Check basic criteria fields
      if (criteria.type !== '' || criteria.condition !== '') {
        return true;
      }

      // Check value fields based on type
      const valueType = this.getValueTypeForCondition(criteria.type, criteria.condition);
      switch (valueType) {
        case 'Text':
        case 'days':
          return criteria.value !== null && criteria.value !== '';
        case 'date':
          return criteria.value !== null;
        case 'date_range':
          return criteria.startDate !== null || criteria.endDate !== null;
        case 'package':
          return criteria.selectedPackages && criteria.selectedPackages.length > 0;
        case 'car_wash_type':
          return criteria.selectedWashTypes && criteria.selectedWashTypes.length > 0;
        case 'vehicle_make':
          return criteria.selectedVehicleMakes && criteria.selectedVehicleMakes.length > 0;
        default:
          return false;
      }
    });
  }

  /**
   * Adds a new empty criteria object to segmentForm.criteria and appends a default 'AND' logical operator.
   * @example
   * nAddCriteria()
   * undefined
   * @param {void} none - No arguments.
   * @returns {void} No return value.
   **/
  onAddCriteria() {
    this.segmentForm.criteria.push({
      type: '',
      condition: '',
      value: null,
      startDate: null,
      endDate: null,
      selectedPackages: [] as string[],
      selectedWashTypes: [] as string[],
      selectedVehicleMakes: [] as string[],
    });
    // Add a logical operator for the new criteria (default to AND)
    this.segmentForm.logicalOperators.push('AND');
  }

  onRemoveCriteria(index: number) {
    const n = this.segmentForm.criteria.length;
    if (n <= 1) {
      return;
    }
    this.segmentForm.criteria.splice(index, 1);
    if (this.segmentForm.logicalOperators.length > 0) {
      const opIndex = index === 0 ? 0 : index - 1;
      this.segmentForm.logicalOperators.splice(opIndex, 1);
    }
  }

  onLogicalOperatorChange(index: number, operator: string) {
    this.segmentForm.logicalOperators[index] = operator;
  }

  // Handle criteria type change
  onCriteriaTypeChange(index: number, criteriaType: string) {
    this.segmentForm.criteria[index].type = criteriaType;
    // Reset condition and value when criteria type changes
    this.segmentForm.criteria[index].condition = '';
    this.segmentForm.criteria[index].value = '';
  }

  // Handle condition change
  onConditionChange(index: number, condition: string) {
    this.segmentForm.criteria[index].condition = condition;
    // Reset value when condition changes
    this.segmentForm.criteria[index].value = '';
  }

  // Handle package selection change
  /**
   * Toggle a package value in the selectedPackages array for a given criteria index.
   * @example
   * nPackageSelectionChange(0, 'premium', { checked: true })
   * // adds 'premium' to segmentForm.criteria[0].selectedPackages
   * @param {{number}} {{index}} - Index of the criteria in segmentForm.criteria.
   * @param {{string}} {{packageValue}} - Package value to add or remove from the selectedPackages array.
   * @param {{any}} {{event}} - Event object containing a 'checked' boolean indicating selection state.
   * @returns {{void}} Void - Does not return a value.
   **/
  onPackageSelectionChange(index: number, packageValue: string, event: any) {
    if (!this.segmentForm.criteria[index].selectedPackages) {
      this.segmentForm.criteria[index].selectedPackages = [];
    }

    if (event.checked) {
      if (!this.segmentForm.criteria[index].selectedPackages.includes(packageValue)) {
        this.segmentForm.criteria[index].selectedPackages.push(packageValue);
      }
    } else {
      const packageIndex = this.segmentForm.criteria[index].selectedPackages.indexOf(packageValue);
      if (packageIndex > -1) {
        this.segmentForm.criteria[index].selectedPackages.splice(packageIndex, 1);
      }
    }
  }

  // Handle car wash type selection change
  /**
   * Updates the selectedWashTypes array for a segment criteria when a wash type checkbox changes.
   * @example
   * nWashTypeSelectionChange(0, 'dry_clean', {checked: true})
   * undefined
   * @param {{number}} {{index}} - Index of the criteria segment being updated.
   * @param {{string}} {{washTypeValue}} - The wash type value to add or remove.
   * @param {{any}} {{event}} - Event object containing a checked boolean indicating selection state.
   * @returns {{void}} No return value.
   **/
  onWashTypeSelectionChange(index: number, washTypeValue: string, event: any) {
    if (!this.segmentForm.criteria[index].selectedWashTypes) {
      this.segmentForm.criteria[index].selectedWashTypes = [];
    }

    if (event.checked) {
      if (!this.segmentForm.criteria[index].selectedWashTypes.includes(washTypeValue)) {
        this.segmentForm.criteria[index].selectedWashTypes.push(washTypeValue);
      }
    } else {
      const washTypeIndex =
        this.segmentForm.criteria[index].selectedWashTypes.indexOf(washTypeValue);
      if (washTypeIndex > -1) {
        this.segmentForm.criteria[index].selectedWashTypes.splice(washTypeIndex, 1);
      }
    }
  }

  // Handle vehicle make selection change
  /**
   * Toggle a vehicle make selection for a specific criteria index by adding or removing it from selectedVehicleMakes.
   * @example
   * nVehicleMakeSelectionChange(0, 'Toyota', { checked: true })
   * undefined
   * @param {{number}} {{index}} - Index of the criteria entry to update.
   * @param {{string}} {{vehicleMakeValue}} - Vehicle make value to add or remove.
   * @param {{any}} {{event}} - Event object with a boolean 'checked' property indicating selection state.
   * @returns {{void}} Does not return a value.
   **/
  onVehicleMakeSelectionChange(index: number, vehicleMakeValue: string, event: any) {
    if (!this.segmentForm.criteria[index].selectedVehicleMakes) {
      this.segmentForm.criteria[index].selectedVehicleMakes = [];
    }

    if (event.checked) {
      if (!this.segmentForm.criteria[index].selectedVehicleMakes.includes(vehicleMakeValue)) {
        this.segmentForm.criteria[index].selectedVehicleMakes.push(vehicleMakeValue);
      }
    } else {
      const vehicleMakeIndex =
        this.segmentForm.criteria[index].selectedVehicleMakes.indexOf(vehicleMakeValue);
      if (vehicleMakeIndex > -1) {
        this.segmentForm.criteria[index].selectedVehicleMakes.splice(vehicleMakeIndex, 1);
      }
    }
  }

  // Get filtered vehicle makes based on search term
  getFilteredVehicleMakes(criteriaIndex: number) {
    let filtered = this.vehicleMakeOptions;

    if (this.vehicleMakeSearchTerm.trim()) {
      filtered = this.vehicleMakeOptions.filter(option =>
        option.label.toLowerCase().includes(this.vehicleMakeSearchTerm.toLowerCase())
      );
    }

    return filtered;
  }

  // Get paginated vehicle makes for display
  /**
   * Returns a paginated list of vehicle makes for a given criteria index, prioritizing selected makes in edit mode.
   * @example
   * etPaginatedVehicleMakes(0)
   * [{ label: 'Ford', value: 'FORD' }, { label: 'Toyota', value: 'TOY' }]
   * @param {{number}} {{criteriaIndex}} - Index of the criteria within the segment form to retrieve vehicle makes for.
   * @returns {{Array<Object>}} Return an array of vehicle make objects (each typically containing label and value), either paginated or complete based on component state.
   **/
  getPaginatedVehicleMakes(criteriaIndex: number) {
    const filtered = this.getFilteredVehicleMakes(criteriaIndex);
    const criteria = this.segmentForm.criteria[criteriaIndex];

    // In edit mode, prioritize selected vehicles
    if (
      this.isEditMode &&
      criteria.selectedVehicleMakes &&
      criteria.selectedVehicleMakes.length > 0
    ) {
      const selectedVehicles = filtered.filter(vehicle =>
        criteria.selectedVehicleMakes.includes(vehicle.value)
      );
      const unselectedVehicles = filtered.filter(
        vehicle => !criteria.selectedVehicleMakes.includes(vehicle.value)
      );

      // Combine selected first, then unselected
      const prioritizedList = [...selectedVehicles, ...unselectedVehicles];

      if (this.vehicleMakeShowAll) {
        return prioritizedList;
      }

      return prioritizedList.slice(0, this.vehicleMakeItemsPerPage);
    }

    if (this.vehicleMakeShowAll) {
      return filtered;
    }

    return filtered.slice(0, this.vehicleMakeItemsPerPage);
  }

  // Get remaining vehicle make count
  /**
   * Calculates how many additional vehicle makes remain beyond the configured items-per-page for a given criteria index.
   * @example
   * etRemainingVehicleMakeCount(0)
   * 2
   * @param {{number}} {{criteriaIndex}} - Index of the criteria in segmentForm used to filter and prioritize vehicle makes.
   * @returns {{number}} Number of remaining vehicle makes beyond vehicleMakeItemsPerPage (always non-negative).
   **/
  getRemainingVehicleMakeCount(criteriaIndex: number) {
    const filtered = this.getFilteredVehicleMakes(criteriaIndex);
    const criteria = this.segmentForm.criteria[criteriaIndex];

    // In edit mode, calculate remaining based on prioritized list
    if (
      this.isEditMode &&
      criteria.selectedVehicleMakes &&
      criteria.selectedVehicleMakes.length > 0
    ) {
      const selectedVehicles = filtered.filter(vehicle =>
        criteria.selectedVehicleMakes.includes(vehicle.value)
      );
      const unselectedVehicles = filtered.filter(
        vehicle => !criteria.selectedVehicleMakes.includes(vehicle.value)
      );
      const prioritizedList = [...selectedVehicles, ...unselectedVehicles];
      const remaining = prioritizedList.length - this.vehicleMakeItemsPerPage;
      return remaining > 0 ? remaining : 0;
    }

    const remaining = filtered.length - this.vehicleMakeItemsPerPage;
    return remaining > 0 ? remaining : 0;
  }

  // Show all vehicle makes
  showAllVehicleMakes() {
    this.vehicleMakeShowAll = true;
  }

  // Reset vehicle make search and pagination
  resetVehicleMakeSearch() {
    this.vehicleMakeSearchTerm = '';
    this.vehicleMakeShowAll = false;
  }

  // Handle vehicle make search
  onVehicleMakeSearchChange() {
    this.vehicleMakeShowAll = false; // Reset to paginated view when searching
  }

  // Clear vehicle make search
  onClearVehicleMakeSearch() {
    this.vehicleMakeSearchTerm = '';
    this.vehicleMakeShowAll = false;
  }

  // Check if there are no filtered vehicle make results
  hasNoVehicleMakeResults(): boolean {
    if (!this.vehicleMakeSearchTerm || this.vehicleMakeSearchTerm.trim() === '') {
      return false;
    }
    const filtered = this.getFilteredVehicleMakes(0);
    return filtered.length === 0;
  }

  /**
   * Reset the segment form to its default initial state and clear any stored original form data.
   * @example
   * resetForm()
   * void
   * @returns {{void}} No return value.
   **/
  private resetForm(
    options: { preserveUploadMethod?: boolean; keepOriginalFormData?: boolean } = {}
  ) {
    const keepUpload = Boolean(options.preserveUploadMethod);
    this.segmentForm = {
      name: '',
      description: '',
      criteria: keepUpload
        ? []
        : [
            {
              type: '',
              condition: '',
              value: null,
              startDate: null,
              endDate: null,
              selectedPackages: [] as string[],
              selectedWashTypes: [] as string[],
              selectedVehicleMakes: [] as string[],
            },
          ],
      logicalOperators: [],
    };
    this.createMethod = keepUpload ? 'upload' : 'rule';
    this.clearSelectedExcelFile();
    if (!options.keepOriginalFormData) {
      this.originalFormData = null;
    }
  }

  private isFormValid(): boolean {
    const nameOk =
      this.segmentForm.name.trim().length >= 2 && this.segmentForm.name.trim().length <= 100;
    const descriptionOk =
      this.segmentForm.description.trim().length >= 2 &&
      this.segmentForm.description.trim().length <= 250;

    if (!nameOk || !descriptionOk) {
      return false;
    }

    if (this.createMethod === 'upload') {
      if (this.isEditMode) {
        return true;
      }
      return !!this.selectedExcelFile && !this.excelFileError;
    }

    return this.segmentForm.criteria.every(c => this.isCriteriaValid(c));
  }

  // Enhanced validation with detailed error messages
  /**
   * Validate the component's segmentForm and return an array of error messages describing any validation failures.
   * @example
   * getValidationErrors()
   * ['Segment name is required', 'Description must be at least 2 characters long', 'Criteria 1: Please select a condition']
   * @returns {{string[]}} Array of validation error messages.
   **/
  private getValidationErrors(): string[] {
    const errors: string[] = [];

    if (this.segmentForm.name.trim() === '') {
      errors.push('Segment name is required');
    } else if (this.segmentForm.name.trim().length < 2) {
      errors.push('Segment name must be at least 2 characters long');
    } else if (this.segmentForm.name.trim().length > 100) {
      errors.push('Segment name must not exceed 100 characters');
    }

    if (this.segmentForm.description.trim() === '') {
      errors.push('Description is required');
    } else if (this.segmentForm.description.trim().length < 2) {
      errors.push('Description must be at least 2 characters long');
    } else if (this.segmentForm.description.trim().length > 250) {
      errors.push('Description must not exceed 250 characters');
    }

    if (this.createMethod === 'upload') {
      if (!this.isEditMode) {
        if (!this.selectedExcelFile) {
          errors.push('Please upload an Excel or CSV file');
        } else if (this.excelFileError) {
          errors.push(this.excelFileError);
        }
      }
      return errors;
    }

    this.segmentForm.criteria.forEach((criteria, index) => {
      if (!criteria.type) {
        errors.push(`Criteria ${index + 1}: Please select a criteria type`);
      } else if (!criteria.condition) {
        errors.push(`Criteria ${index + 1}: Please select a condition`);
      } else {
        const valueType = this.getValueTypeForCondition(criteria.type, criteria.condition);
        const criteriaErrors = this.getCriteriaValidationErrors(criteria, valueType, index + 1);
        errors.push(...criteriaErrors);
      }
    });

    return errors;
  }

  /**
   * Validate a single criteria object for the specified value type and return any validation errors as strings.
   * @example
   * getCriteriaValidationErrors({ value: '', selectedPackages: [] }, 'package', 1)
   * ['Criteria 1: Please select at least one package']
   * @param {{any}} {{criteria}} - Criteria object containing the fields to validate (value, startDate, endDate, selectedPackages, etc.).
   * @param {{string}} {{valueType}} - The type of the criteria value (e.g. 'Text', 'days', 'date', 'date_range', 'package', 'car_wash_type', 'vehicle_make').
   * @param {{number}} {{criteriaNumber}} - The 1-based index of the criteria used in error messages.
   * @returns {{string[]}} Array of validation error messages for the provided criteria.
   **/
  private getCriteriaValidationErrors(
    criteria: any,
    valueType: string,
    criteriaNumber: number
  ): string[] {
    const errors: string[] = [];

    switch (valueType) {
      case 'Text':
      case 'days':
        if (!criteria.value || criteria.value.toString().trim() === '') {
          errors.push(`Criteria ${criteriaNumber}: Value is required`);
        } else if (
          valueType === 'days' &&
          (isNaN(Number(criteria.value)) || Number(criteria.value) < 1)
        ) {
          errors.push(
            `Criteria ${criteriaNumber}: Please enter a valid number of days (minimum 1)`
          );
        }
        break;
      case 'date':
        if (!criteria.value) {
          errors.push(`Criteria ${criteriaNumber}: Date is required`);
        } else if (!this.isValidDate(criteria.value)) {
          errors.push(`Criteria ${criteriaNumber}: Please enter a valid date`);
        }
        break;
      case 'date_range':
        if (!criteria.startDate) {
          errors.push(`Criteria ${criteriaNumber}: Start date is required`);
        } else if (!this.isValidDate(criteria.startDate)) {
          errors.push(`Criteria ${criteriaNumber}: Please enter a valid start date`);
        }
        if (!criteria.endDate) {
          errors.push(`Criteria ${criteriaNumber}: End date is required`);
        } else if (!this.isValidDate(criteria.endDate)) {
          errors.push(`Criteria ${criteriaNumber}: Please enter a valid end date`);
        }
        if (
          criteria.startDate &&
          criteria.endDate &&
          new Date(criteria.startDate) > new Date(criteria.endDate)
        ) {
          errors.push(`Criteria ${criteriaNumber}: Start date must be before end date`);
        }
        break;
      case 'package':
        if (!criteria.selectedPackages || criteria.selectedPackages.length === 0) {
          errors.push(`Criteria ${criteriaNumber}: Please select at least one package`);
        }
        break;
      case 'car_wash_type':
        if (!criteria.selectedWashTypes || criteria.selectedWashTypes.length === 0) {
          errors.push(`Criteria ${criteriaNumber}: Please select at least one car wash type`);
        }
        break;
      case 'vehicle_make':
        if (!criteria.selectedVehicleMakes || criteria.selectedVehicleMakes.length === 0) {
          errors.push(`Criteria ${criteriaNumber}: Please select at least one vehicle make`);
        }
        break;
    }

    return errors;
  }

  /**
   * Validate a criteria object based on its type and condition.
   * @example
   * isCriteriaValid({ type: 'date', condition: 'before', value: '2021-01-01' })
   * true
   * @param {{any}} {{criteria}} - Criteria object containing type, condition and related value fields.
   * @returns {{boolean}} True if the provided criteria is valid for its type and condition, otherwise false.
   **/
  private isCriteriaValid(criteria: any): boolean {
    if (!criteria.type || !criteria.condition) {
      return false;
    }

    const valueType = this.getValueTypeForCondition(criteria.type, criteria.condition);

    switch (valueType) {
      case 'Text':
      case 'days':
        return criteria.value !== null && criteria.value !== '';
      case 'date':
        return criteria.value !== null && this.isValidDate(criteria.value);
      case 'date_range':
        return (
          criteria.startDate !== null &&
          criteria.endDate !== null &&
          this.isValidDate(criteria.startDate) &&
          this.isValidDate(criteria.endDate)
        );
      case 'package':
        return criteria.selectedPackages && criteria.selectedPackages.length > 0;
      case 'car_wash_type':
        return criteria.selectedWashTypes && criteria.selectedWashTypes.length > 0;
      case 'vehicle_make':
        return criteria.selectedVehicleMakes && criteria.selectedVehicleMakes.length > 0;
      default:
        return false;
    }
  }

  get isFormValidState(): boolean {
    return this.isFormValid();
  }

  // Check if form has changes in edit mode
  get hasFormChanges(): boolean {
    if (!this.isEditMode || !this.originalFormData) {
      return false;
    }

    return this.detectFormChanges();
  }

  // Detect changes between original and current form data
  /**
   * Determine whether the current segmentForm has any differences compared to originalFormData.
   * @example
   * this.detectFormChanges()
   * true
   * @returns {{boolean}} True if any field (name, description, criteria list/count, individual criteria fields or arrays, or logical operators) differs from the original; false if no changes or originalFormData is not set.
   **/
  private detectFormChanges(): boolean {
    if (!this.originalFormData) return false;

    // Check basic fields
    if (
      this.segmentForm.name !== this.originalFormData.name ||
      this.segmentForm.description !== this.originalFormData.description
    ) {
      return true;
    }

    // Check criteria changes
    if (this.segmentForm.criteria.length !== this.originalFormData.criteria.length) {
      return true;
    }

    // Check each criteria for changes
    for (let i = 0; i < this.segmentForm.criteria.length; i += 1) {
      const current = this.segmentForm.criteria[i];
      const original = this.originalFormData.criteria[i];

      if (!original) return true;

      if (
        current.type !== original.type ||
        current.condition !== original.condition ||
        current.value !== original.value ||
        current.startDate !== original.startDate ||
        current.endDate !== original.endDate
      ) {
        return true;
      }

      // Check array fields
      if (
        JSON.stringify(current.selectedPackages) !== JSON.stringify(original.selectedPackages) ||
        JSON.stringify(current.selectedWashTypes) !== JSON.stringify(original.selectedWashTypes) ||
        JSON.stringify(current.selectedVehicleMakes) !==
          JSON.stringify(original.selectedVehicleMakes)
      ) {
        return true;
      }
    }

    // Check logical operators
    if (
      JSON.stringify(this.segmentForm.logicalOperators) !==
      JSON.stringify(this.originalFormData.logicalOperators)
    ) {
      return true;
    }

    return false;
  }

  // Helper methods to check if validation errors should be shown
  shouldShowNameError(): boolean {
    return (
      this.touchedFields.name &&
      (this.segmentForm.name.trim() === '' ||
        this.segmentForm.name.trim().length < 2 ||
        this.segmentForm.name.trim().length > 100)
    );
  }

  shouldShowDescriptionError(): boolean {
    return (
      this.touchedFields.description &&
      (this.segmentForm.description.trim() === '' ||
        this.segmentForm.description.trim().length < 2 ||
        this.segmentForm.description.trim().length > 250)
    );
  }

  shouldShowDateError(criteriaIndex: number, field: string, value: string | Date | null): boolean {
    const touched =
      this.touchedCriteria[criteriaIndex] && this.touchedCriteria[criteriaIndex][field];
    return Boolean(touched && value && !this.isValidDate(value));
  }

  shouldShowDateRangeError(
    criteriaIndex: number,
    startDate: string | Date | null,
    endDate: string | Date | null
  ): boolean {
    const touched =
      this.touchedCriteria[criteriaIndex] &&
      this.touchedCriteria[criteriaIndex]['startDate'] &&
      this.touchedCriteria[criteriaIndex]['endDate'];
    if (
      !touched ||
      !startDate ||
      !endDate ||
      !this.isValidDate(startDate) ||
      !this.isValidDate(endDate)
    ) {
      return false;
    }
    const start = startDate instanceof Date ? startDate : new Date(startDate);
    const end = endDate instanceof Date ? endDate : new Date(endDate);
    return start > end;
  }

  // Helper method to validate date format
  isValidDate(dateValue: string | Date | null): boolean {
    if (!dateValue) return false;
    if (dateValue instanceof Date) {
      return !isNaN(dateValue.getTime());
    }
    const date = new Date(dateValue);
    return date instanceof Date && !isNaN(date.getTime());
  }

  private destroy$ = new Subject<void>();

  constructor(
    private apiService: ApiService,
    private messageService: NotificationService,
    private packageService: PackageService
  ) {}

  ngOnDestroy(): void {
    this.activeCriteriaSelect = null;
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.loadPackages();
    // Check if edit mode data is already available on init
    if (this.isEditMode && this.segmentToEdit) {
      this.populateFormWithSegmentData();
    }
  }

  /**
   * Handle changes to segmentToEdit or isEditMode inputs and update or reset the form state accordingly.
   * @example
   * gOnChanges({ segmentToEdit: { name: 'Test' }, isEditMode: true })
   * void
   * @param {{SimpleChanges}} {{changes}} - Object containing the input property changes provided by Angular.
   * @returns {{void}} Nothing is returned.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    // Check if segmentToEdit changed or isEditMode changed
    if (changes['segmentToEdit'] || changes['isEditMode']) {
      if (this.isEditMode && this.segmentToEdit) {
        this.createMethod = this.segmentToEdit.source_type === 'upload' ? 'upload' : 'rule';
        this.clearSelectedExcelFile();
        this.populateFormWithSegmentData();
      } else if (!this.isEditMode) {
        // Reset form when switching from edit to add mode
        this.resetForm();
        this.touchedFields = {
          name: false,
          description: false,
        };
        this.touchedCriteria = {};
        this.originalFormData = null;
      }
    }
  }

  /**
   * Populate the segment form (this.segmentForm) from this.segmentToEdit or initialize defaults when no segment is present. This fills basic segment fields (name, description), reconstructs criteria and logical operators based on the stored criteria_list (mapping criteria types, conditions, values, date parsing, and multi-select types like packages, wash types, and vehicle makes), ensures at least one empty criteria with a default 'AND' operator when none exist, and finally stores the original form data for change detection.
   * @example
   * populateFormWithSegmentData()
   * undefined
   * @returns {void} No return value; the method updates component state (this.segmentForm) and calls storeOriginalFormData().
   */
  private populateFormWithSegmentData(): void {
    if (!this.segmentToEdit) return;

    // Populate basic segment information
    this.segmentForm.name = this.segmentToEdit.name || '';
    this.segmentForm.description = this.segmentToEdit.description || '';

    // Clear existing criteria and logical operators
    this.segmentForm.criteria = [];
    this.segmentForm.logicalOperators = [];

    // Populate criteria from segment data
    if (this.segmentToEdit.criteria_list && this.segmentToEdit.criteria_list.length > 0) {
      this.segmentToEdit.criteria_list.forEach((criteria: any, index: number) => {
        const criteriaType = this.getCriteriaTypeFromLabel(criteria.criteria);
        const condition = criteria.criteria_condition || '';
        const valueType = this.getValueTypeForCondition(criteriaType, condition);

        // Initialize criteria form with defaults
        const criteriaForm: any = {
          type: criteriaType,
          condition: condition,
          value: null,
          startDate: null,
          endDate: null,
          selectedPackages: [] as string[],
          selectedWashTypes: [] as string[],
          selectedVehicleMakes: [] as string[],
        };

        // Populate fields based on value type
        switch (valueType) {
          case 'Text':
          case 'days':
            criteriaForm.value = criteria.criteria_value || criteria.days || null;
            break;
          case 'date':
            if (criteria.start_date) {
              criteriaForm.value = this.isValidDate(criteria.start_date)
                ? new Date(criteria.start_date)
                : criteria.start_date;
            }
            break;
          case 'date_range':
            if (criteria.start_date) {
              criteriaForm.startDate = this.isValidDate(criteria.start_date)
                ? new Date(criteria.start_date)
                : criteria.start_date;
            }
            if (criteria.end_date) {
              criteriaForm.endDate = this.isValidDate(criteria.end_date)
                ? new Date(criteria.end_date)
                : criteria.end_date;
            }
            break;
          case 'package':
            criteriaForm.selectedPackages = Array.isArray(criteria.criteria_types)
              ? [...criteria.criteria_types]
              : [];
            break;
          case 'car_wash_type':
            criteriaForm.selectedWashTypes = Array.isArray(criteria.criteria_types)
              ? [...criteria.criteria_types]
              : [];
            break;
          case 'vehicle_make':
            criteriaForm.selectedVehicleMakes = Array.isArray(criteria.criteria_types)
              ? [...criteria.criteria_types]
              : [];
            break;
        }

        this.segmentForm.criteria.push(criteriaForm);

        // join_rule on row i is how row i combines with row i+1 (matches segment-details connector)
        const list = this.segmentToEdit.criteria_list!;
        if (index < list.length - 1) {
          this.segmentForm.logicalOperators.push(criteria.join_rule || 'AND');
        }
      });
    } else if (this.segmentToEdit.source_type !== 'upload') {
      // If no criteria, add one empty criteria
      this.segmentForm.criteria = [
        {
          type: '',
          condition: '',
          value: null,
          startDate: null,
          endDate: null,
          selectedPackages: [],
          selectedWashTypes: [],
          selectedVehicleMakes: [],
        },
      ];
      this.segmentForm.logicalOperators = [];
    } else {
      this.segmentForm.criteria = [];
      this.segmentForm.logicalOperators = [];
    }

    // Store original form data for change detection in edit mode
    this.storeOriginalFormData();
  }

  // Store original form data for change detection
  private storeOriginalFormData(): void {
    this.originalFormData = {
      name: this.segmentForm.name,
      description: this.segmentForm.description,
      criteria: JSON.parse(JSON.stringify(this.segmentForm.criteria)),
      logicalOperators: [...this.segmentForm.logicalOperators],
    };
  }

  /**
   * Map a human-readable criteria label to its internal snake_case criteria type, or return the input if already formatted.
   * @example
   * getCriteriaTypeFromLabel('Total Spend')
   * 'total_spend'
   * @param {{string}} {{criteriaLabel}} - Criteria label (human-readable) to convert to a criteria type.
   * @returns {{string}} Return the snake_case criteria type, the original value if it already contains an underscore, or an empty string if falsy/unknown.
   **/
  private getCriteriaTypeFromLabel(criteriaLabel: string): string {
    // If it's already a value (contains underscore), return as-is
    if (criteriaLabel && criteriaLabel.includes('_')) {
      return criteriaLabel;
    }

    // Otherwise, map from label to value
    const labelMap: Record<string, string> = {
      'total spend': 'total_spend',
      'visit frequency': 'visit_frequency',
      'package name': 'package_name',
      'car wash type': 'car_wash_type',
      'last visit date': 'last_visit_date',
      'vehicle make': 'vehicle_make',
    };
    return labelMap[criteriaLabel.toLowerCase()] || criteriaLabel || '';
  }

  /**
   * Loads packages from the packageService, filters for active "car_wash" packages, and populates this.packageOptions with { label, value } pairs.
   * @example
   * this.loadPackages()
   * // After calling, this.packageOptions will contain options for active car wash packages
   * @returns {void} Does not return a value; updates component state (this.packageOptions).
   **/
  private loadPackages(): void {
    this.packageService.loadPackages();
    this.packageService.packages$
      .pipe(takeUntil(this.destroy$))
      .subscribe((packages: Package[]) => {
        // Filter packages by status: true, type: "car_wash", and package_status: true
        const filteredPackages = packages.filter(
          pkg => pkg.status === true && pkg.type === 'car_wash' && pkg.package_status === true
        );

        this.packageOptions = filteredPackages.map(pkg => ({
          label: pkg.package_name,
          value: pkg.id,
        }));
      });
  }

  // Convert Date object to local time string format (YYYY-MM-DDTHH:mm:ss)
  /**
   * Convert a Date or date string to a local time string in 'YYYY-MM-DDTHH:mm:ss' format, or return null for invalid or missing input.
   * @example
   * formatDateToLocalTime(new Date(2023, 2, 5, 12, 34, 56))
   * "2023-03-05T12:34:56"
   * @param {{Date | string | null}} {{date}} - The input date as a Date object, an ISO date string, or null.
   * @returns {{string | null}} The formatted local datetime string in 'YYYY-MM-DDTHH:mm:ss' format, or null if the input is falsy or invalid.
   **/
  private formatDateToLocalTime(date: Date | string | null): string | null {
    if (!date) return null;

    const dateObj = date instanceof Date ? date : new Date(date);

    // Check if date is valid
    if (isNaN(dateObj.getTime())) return null;

    // Format as local time: YYYY-MM-DDTHH:mm:ss
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const hours = String(dateObj.getHours()).padStart(2, '0');
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
    const seconds = String(dateObj.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  }

  private resolveSegmentCriteriaTimezone(): string {
    return getUserTimezone();
  }

  // Transform form data to API payload structure
  /**
   * Transform the component's segment form state into the API payload shape expected by the backend.
   * @example
   * transformFormDataToApiPayload()
   * {
   *   name: "VIP Customers",
   *   description: "Customers who visited in the last 30 days or purchased premium package",
   *   segment_status: true,
   *   criteria_list: [
   *     { criteria: "last_visit_date", criteria_condition: "gte", start_date: "2026-02-15T00:00:00Z", timezone: "America/Los_Angeles", join_rule: "OR" },
   *     { criteria: "package", criteria_condition: "in", criteria_types: ["premium","gold"], join_rule: "AND" }
   *   ]
   * }
   * @returns {any} The formatted payload object containing name, description, segment_status and criteria_list.
   **/
  private transformFormDataToApiPayload(): any {
    const criteriaList = this.segmentForm.criteria.map((criteria, index) => {
      const baseCriteria = {
        criteria: criteria.type,
        criteria_condition: criteria.condition,
      };

      const valueType = this.getValueTypeForCondition(criteria.type, criteria.condition);

      // join_rule on row i is how criterion i combines with i+1 (API / segment-details use same semantics)
      const joinRule =
        index < this.segmentForm.criteria.length - 1
          ? this.segmentForm.logicalOperators[index] || 'AND'
          : 'AND';

      let criteriaWithValue;
      switch (valueType) {
        case 'Text':
          criteriaWithValue = {
            ...baseCriteria,
            criteria_value: criteria.value,
          };
          break;
        case 'days':
          criteriaWithValue = {
            ...baseCriteria,
            days: criteria.value,
          };
          break;
        case 'date':
          criteriaWithValue = {
            ...baseCriteria,
            start_date: this.formatDateToLocalTime(criteria.value),
            ...(criteria.type === 'last_visit_date' && {
              timezone: this.resolveSegmentCriteriaTimezone(),
            }),
          };
          break;
        case 'date_range':
          criteriaWithValue = {
            ...baseCriteria,
            start_date: this.formatDateToLocalTime(criteria.startDate),
            end_date: this.formatDateToLocalTime(criteria.endDate),
            ...(criteria.type === 'last_visit_date' && {
              timezone: this.resolveSegmentCriteriaTimezone(),
            }),
          };
          break;
        case 'package':
          criteriaWithValue = {
            ...baseCriteria,
            criteria_types: criteria.selectedPackages,
          };
          break;
        case 'car_wash_type':
          criteriaWithValue = {
            ...baseCriteria,
            criteria_types: criteria.selectedWashTypes,
          };
          break;
        case 'vehicle_make':
          criteriaWithValue = {
            ...baseCriteria,
            criteria_types: criteria.selectedVehicleMakes,
          };
          break;
        default:
          criteriaWithValue = baseCriteria;
      }

      // Add join_rule to each criteria item
      return {
        ...criteriaWithValue,
        join_rule: joinRule,
      };
    });

    return {
      name: this.segmentForm.name,
      description: this.segmentForm.description,
      segment_status: true,
      criteria_list: criteriaList,
    };
  }

  // Get human-readable criteria label
  private getCriteriaLabel(criteriaType: string): string {
    const criteria = this.criteriaOptions.find(option => option.value === criteriaType);
    return criteria ? criteria.label : criteriaType;
  }

  /** Validates the form and, if valid, sets isLoading and returns the API payload. Returns null when validation fails. */
  private validateAndPreparePayload(): ReturnType<
    typeof this.transformFormDataToApiPayload
  > | null {
    const validationErrors = this.getValidationErrors();
    if (validationErrors.length > 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: sanitizeErrorMessage(validationErrors.join('. ')),
      });
      return null;
    }
    this.isLoading = true;
    return this.transformFormDataToApiPayload();
  }

  // Update segment via API
  /**
   * Update an existing user segment: validate and prepare payload, send a PUT request to update the segment, handle success and error responses, emit events, reset the form, and manage loading state.
   * @example
   * pdateSegment()
   * void
   * @returns {void} No return value.
   **/
  updateSegment(): void {
    const payload = this.validateAndPreparePayload();
    if (payload === null) return;

    this.apiService.put(`segment/${this.segmentToEdit.id}`, payload).subscribe({
      next: response => {
        this.isLoading = false;
        if (response.status) {
          this.messageService.add({
            severity: 'success',
            summary: 'User segment updated!',
            detail: `"${this.segmentForm.name}" has been updated successfully`,
          });
          this.save.emit(response.data);
          this.resetForm();
          this.close.emit();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: sanitizeErrorMessage(response.message || 'Failed to update segment.'),
          });
        }
      },
      error: error => {
        this.isLoading = false;
        console.error('Error updating segment:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: sanitizeErrorMessage(
            error.error?.message || 'An error occurred while updating the segment.'
          ),
        });
      },
    });
  }

  // Create segment via API
  /**
   * Validates and prepares the segment payload, posts it to the 'segment' API endpoint, handles success and error notifications, emits events on success, and resets the form.
   * @example
   * createSegment()
   * // Emits 'save' with response.data and 'close' on success; shows success/error messages; returns void.
   * @returns {void} Performs side effects: network request, message toasts, event emissions, and form reset.
   */
  createSegment(): void {
    const payload = this.validateAndPreparePayload();
    if (payload === null) return;

    this.apiService.post('segment', payload).subscribe({
      next: response => {
        this.isLoading = false;
        if (response.status) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Segment created successfully!',
          });
          this.save.emit(response.data);
          this.resetForm();
          this.close.emit();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: sanitizeErrorMessage(response.message || 'Failed to create segment.'),
          });
        }
      },
      error: error => {
        this.isLoading = false;
        console.error('Error creating segment:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: sanitizeErrorMessage(
            error.error?.message || 'An error occurred while creating the segment.'
          ),
        });
      },
    });
  }

  /**
   * Create segment from Excel/CSV: matched CRM customers → Postgres;
   * non-customers → Mongo campaign_contacts + campaign_segment_mappings.
   */
  private createUploadSegment(): void {
    const validationErrors = this.getValidationErrors();
    if (validationErrors.length > 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: sanitizeErrorMessage(validationErrors.join('. ')),
      });
      return;
    }

    if (!this.selectedExcelFile) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please upload an Excel or CSV file',
      });
      return;
    }

    const formData = new FormData();
    formData.append('name', this.segmentForm.name.trim());
    formData.append('description', (this.segmentForm.description || '').trim());
    formData.append('segment_status', 'true');
    formData.append('file', this.selectedExcelFile, this.selectedExcelFile.name);

    this.isLoading = true;
    this.apiService.post('segment/upload', formData).subscribe({
      next: response => {
        this.isLoading = false;
        if (response.status) {
          this.messageService.add({
            severity: 'success',
            summary: 'Upload started',
            detail: response.message || 'Segment created. Excel is processing in the background.',
          });
          this.save.emit(response.data);
          this.resetForm();
          this.close.emit();
        } else {
          this.excelFileError = sanitizeErrorMessage(
            response.message || 'Failed to create segment from upload.'
          );
        }
      },
      error: error => {
        this.isLoading = false;
        console.error('Error creating upload segment:', error);
        // Show under the upload control only — avoid a duplicate snackbar toast.
        this.excelFileError = sanitizeErrorMessage(
          error.error?.message || 'An error occurred while creating the segment from upload.'
        );
      },
    });
  }

  private setSelectedExcelFile(file: File): void {
    const extension = this.getFileExtension(file.name);
    if (!EXCEL_ACCEPT_EXTENSIONS.includes(extension)) {
      this.selectedExcelFile = null;
      this.excelFileError = 'Please upload a .xlsx or .csv file';
      return;
    }
    if (file.size > EXCEL_MAX_BYTES) {
      this.selectedExcelFile = null;
      this.excelFileError = 'File must be 10MB or smaller';
      return;
    }
    this.selectedExcelFile = file;
    this.excelFileError = null;
  }

  private getFileExtension(fileName: string): string {
    const idx = fileName.lastIndexOf('.');
    if (idx < 0) {
      return '';
    }
    return fileName.slice(idx).toLowerCase();
  }

  private neutralizeSpreadsheetCell(value: string): string {
    if (value && /^[=+\-@\t\r]/.test(value)) {
      return `'${value}`;
    }
    return value;
  }

  private downloadWorkbook(rows: Record<string, string>[], fileName: string): void {
    const safeRows = rows.map(row =>
      Object.fromEntries(
        Object.entries(row).map(([key, cell]) => [key, this.neutralizeSpreadsheetCell(cell ?? '')])
      )
    );
    const worksheet = XLSX.utils.json_to_sheet(safeRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Leads');
    XLSX.writeFile(workbook, fileName);
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): unknown {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): unknown {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): unknown {
    return s;
  }
}
